import {
  AlertTriangle,
  Check,
  Eye,
  EyeOff,
  Loader2,
  RefreshCw,
  Sparkles,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { SchemaNovelLabel, SchemaProposal } from '@/services/api/schemaSessions'
import type { ValidationFinding } from '@/services/api/schemas'
import { FindingRow } from '../FindingRow'
import { ProposalEvidence } from './ProposalEvidence'
import { ProposalFindings } from './ProposalFindings'
import { ReviewDisclosure } from './ReviewDisclosure'

export { ProposalEvidence } from './ProposalEvidence'

export interface SchemaAgentReviewProps {
  proposal: SchemaProposal
  review: { hasDraft: boolean; caseOwnsSchema: boolean; viewingDraft: boolean }
  actions: {
    pending: 'accept' | 'discard' | 'repair' | null
    viewDraft: (view: boolean) => void
    accept: (name: string | undefined) => void
    discard: () => void
    repair: (feedback: string) => void
  }
}

function FindingGroup({ title, findings }: { title: string; findings: ValidationFinding[] }) {
  if (!findings.length) return null
  return (
    <ReviewDisclosure title={title} count={findings.length}>
      <div className="space-y-2">
        {findings.map((finding, index) => (
          <FindingRow key={`${finding.location}:${index}`} finding={finding} concept={undefined} />
        ))}
      </div>
    </ReviewDisclosure>
  )
}

function NovelLabelList({ novel }: { novel: SchemaNovelLabel[] }) {
  const { t } = useTranslation()
  if (novel.length === 0) return null
  return (
    <div className="rounded-lg border border-info-border bg-info-surface p-3 text-sm text-info-foreground">
      <p className="flex items-center gap-1.5 font-medium">
        <AlertTriangle className="size-3.5 shrink-0" />
        {t('schemaAgent.review.novel')}
      </p>
      <p className="mt-0.5 text-xs opacity-90">{t('schemaAgent.review.novelHint')}</p>
      <ul className="mt-2 flex flex-col gap-1 text-xs">
        {novel.map(item => (
          <li key={`${item.kind}-${item.label}`} className="flex flex-col">
            <span>
              <code className="font-mono">{item.label}</code> — {item.message}
            </span>
            {item.nearest && (
              <span className="opacity-80">
                {t('schemaAgent.review.nearest', { label: item.nearest })}
              </span>
            )}
            {item.justification && <span className="italic opacity-80">{item.justification}</span>}
          </li>
        ))}
      </ul>
    </div>
  )
}

function ProposalHeader({ proposal }: { proposal: SchemaProposal }) {
  const { t } = useTranslation()
  const schema = proposal.schema
  const count = (key: string) => (Array.isArray(schema[key]) ? schema[key].length : 0)
  return (
    <div className="space-y-3">
      {!proposal.errors.length && (
        <p className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <Sparkles className="size-4 text-primary" />
          {t('schemaAgent.review.title')}
        </p>
      )}
      <h3 className="text-lg font-semibold leading-snug">{proposal.name}</h3>
      {proposal.description && (
        <p className="text-sm leading-relaxed text-muted-foreground">{proposal.description}</p>
      )}
      <p className="text-xs tabular-nums text-muted-foreground">
        {t('schemaAgent.review.counts', {
          entities: count('entity_types'),
          relations: count('relationship_types'),
          patterns: count('patterns'),
        })}
      </p>
    </div>
  )
}

function ReviewActions({
  repair,
  accept,
  actions,
}: {
  repair: boolean
  accept: { enabled: boolean; label: string }
  actions: {
    pending: SchemaAgentReviewProps['actions']['pending']
    discard: () => void
    submit: () => void
  }
}) {
  const { t } = useTranslation()
  const busy = actions.pending !== null
  return (
    <div className="sticky bottom-0 -mx-4 mt-2 flex flex-wrap items-center justify-between gap-2 border-t bg-background px-4 py-4">
      <Button variant="ghost" onClick={actions.discard} disabled={busy}>
        {actions.pending === 'discard' ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Trash2 className="size-4" />
        )}
        {t('schemaAgent.review.discard')}
      </Button>
      <Button
        variant={repair ? 'default' : 'success'}
        onClick={actions.submit}
        disabled={busy || (!repair && !accept.enabled)}
      >
        {actions.pending === 'accept' || actions.pending === 'repair' ? (
          <Loader2 className="size-4 animate-spin" />
        ) : repair ? (
          <RefreshCw className="size-4" />
        ) : (
          <Check className="size-4" />
        )}
        {t(repair ? 'schemaAgent.review.repair' : accept.label)}
      </Button>
    </div>
  )
}

function DraftControls({
  review,
  actions,
  name,
}: {
  review: SchemaAgentReviewProps['review']
  actions: SchemaAgentReviewProps['actions']
  name: { value: string; change: (value: string) => void; placeholder: string }
}) {
  const { t } = useTranslation()
  if (!review.hasDraft) return null
  return (
    <div className="space-y-4">
      <Button
        variant="outline"
        size="sm"
        disabled={actions.pending !== null}
        onClick={() => actions.viewDraft(!review.viewingDraft)}
      >
        {review.viewingDraft ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        {t(review.viewingDraft ? 'schemaAgent.review.hideDraft' : 'schemaAgent.review.viewDraft')}
      </Button>
      {!review.caseOwnsSchema && (
        <div className="space-y-2">
          <Label htmlFor="schema-agent-name">{t('schemaAgent.review.name')}</Label>
          <Input
            id="schema-agent-name"
            value={name.value}
            onChange={e => name.change(e.target.value)}
            placeholder={name.placeholder}
          />
          <p className="text-xs text-muted-foreground">{t('schemaAgent.review.nameHint')}</p>
        </div>
      )}
    </div>
  )
}

function ProposalDetails({ proposal }: { proposal: SchemaProposal }) {
  const { t } = useTranslation()
  return (
    <div>
      {proposal.rationale && (
        <ReviewDisclosure title={t('schemaAgent.review.rationale')}>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
            {proposal.rationale}
          </p>
        </ReviewDisclosure>
      )}
      <ProposalEvidence proposal={proposal} />
      <NovelLabelList novel={proposal.novel} />
      <FindingGroup title={t('schemaAgent.review.warnings')} findings={proposal.warnings} />
    </div>
  )
}

function RepairInstructions({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  disabled: boolean
}) {
  const { t } = useTranslation()
  return (
    <ReviewDisclosure title={t('schemaAgent.review.addInstructions')}>
      <Label htmlFor="schema-repair-feedback" className="sr-only">
        {t('schemaAgent.review.addInstructions')}
      </Label>
      <Textarea
        id="schema-repair-feedback"
        value={value}
        onChange={e => onChange(e.target.value)}
        maxLength={4000}
        rows={3}
        disabled={disabled}
        placeholder={t('schemaAgent.review.feedbackPlaceholder')}
      />
    </ReviewDisclosure>
  )
}

export function SchemaAgentReview({ proposal, review, actions }: SchemaAgentReviewProps) {
  const [name, setName] = useState('')
  const [feedback, setFeedback] = useState('')
  const needsRepair = proposal.errors.length > 0
  return (
    <div className="flex min-w-0 flex-col gap-5">
      <ProposalHeader proposal={proposal} />
      <ProposalFindings proposal={proposal} />
      <ProposalDetails proposal={proposal} />
      {needsRepair && (
        <RepairInstructions
          value={feedback}
          onChange={setFeedback}
          disabled={actions.pending !== null}
        />
      )}
      <DraftControls
        review={review}
        actions={actions}
        name={{ value: name, change: setName, placeholder: proposal.name }}
      />
      <ReviewActions
        repair={needsRepair}
        accept={{
          enabled: review.hasDraft,
          label: review.caseOwnsSchema
            ? 'schemaAgent.review.acceptVersion'
            : 'schemaAgent.review.accept',
        }}
        actions={{
          pending: actions.pending,
          discard: actions.discard,
          submit: () =>
            needsRepair
              ? actions.repair(feedback.trim())
              : actions.accept(name.trim() || undefined),
        }}
      />
    </div>
  )
}
